import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { IconSparkles } from "@tabler/icons-react";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";

export interface CopilotProposalBannerProps {
  /** What the agent is proposing, in the user's terms. */
  message: string;
  /** Removals read destructive: a rose ground and a warning confirm button. */
  isDestructive?: boolean;
  discardLabel: string;
  confirmLabel: string;
  onDiscard: () => void;
  onConfirm: () => void;
  /** Both decisions are held while one is in flight. */
  isDisabled?: boolean;
  className?: string;
}

/**
 * The bar above a settings pane when Copilot has an unresolved proposal for the
 * node, carrying the two decisions the user can make about it. Shared by the
 * criterion, section, and outcome panes, and by both proposal shapes: an edit
 * previews its changes in the form below, while a removal has nothing to
 * preview and leaves the form as it is.
 *
 * Users see "suggestion"; the code says "proposal", after the row it resolves.
 */
export const CopilotProposalBanner: FC<CopilotProposalBannerProps> = ({
  message,
  isDestructive = false,
  discardLabel,
  confirmLabel,
  onDiscard,
  onConfirm,
  isDisabled = false,
  className,
}) => (
  <div
    className={twMerge(
      "-mx-2 -mt-2 flex shrink-0 items-center justify-between rounded-t-lg px-3 py-2",
      isDestructive ? "bg-rose-50" : "bg-indigo-50",
      className,
    )}
  >
    <div
      className={twMerge(
        "flex items-center gap-2 text-sm",
        isDestructive ? "text-rose-900" : "text-indigo-900",
      )}
    >
      <IconSparkles
        className={twMerge(
          "size-4 shrink-0",
          isDestructive ? "text-rose-500" : "text-indigo-500",
        )}
      />
      <span>{message}</span>
    </div>
    <div className="flex items-center gap-2">
      <Button
        variant={ButtonVariant.Tertiary}
        size={Size.MEDIUM}
        onClick={onDiscard}
        isDisabled={isDisabled}
      >
        {discardLabel}
      </Button>
      <Button
        variant={isDestructive ? ButtonVariant.Warning : ButtonVariant.Primary}
        size={Size.MEDIUM}
        onClick={onConfirm}
        isDisabled={isDisabled}
      >
        {confirmLabel}
      </Button>
    </div>
  </div>
);
